import { type FunctionalComponent, h, getAssetPath } from '@stencil/core';
import { numberOfLinks } from '../utils/map-widget-data';
import { widgetStatistics } from '../offsites-widget-statistics';
import type { WidgetComponent } from './offsites-widget-components.d';
import { setTitlePartColor } from '../offsites-widget-v4/widget-logic/offsites-widget-v4-logic';
import type {
  widgetObject,
} from '../offsites-widget-v4/types/offsites-widget-v4.types';

export const mediaWidget = (
  data: WidgetComponent['data'],
  searchStatisticsEventEmitter: WidgetComponent['searchStatisticsEventEmitter'],
  tabName: WidgetComponent['tabName'],
): FunctionalComponent => {
    const backgroundImage = getAssetPath(data.backgroundUrl);

    return (
      <div>
        <div
          class={['uiaas-offsites-widget', `type-${data.widgetType}`].join(' ')}
          style={{'background-image': data.backgroundUrl ? `url(${backgroundImage})` : 'none'}}
        >
          <div class="contents">
            <h3 class="offsites-title">
              <p class={'offsites-title__value'}>{data.titleValue}</p>
              <p class={'offsites-title__value-colored'}>
                {data.titleValueColored}
              </p>
            </h3>
            <p class="offsites-subtitle">{data.subtitleValue}</p>
            <ul>
              {[...Array(numberOfLinks)].map((_e, i) => {
                  const index = i + 1;
                  if (data[`link${index}value`]) {
                    return (
                      <li>
                        <qds-standalone-link
                          target="_blank"
                          rel="external"
                          onMouseDown={(e) => {
                              e.stopPropagation();
                              searchStatisticsEventEmitter(
                                widgetStatistics.sendUserRedirectionStatisticsEvent(
                                  `link${index}`,
                                  data.widgetType,
                                  data.widgetName,
                                  tabName,
                                  e.button == 0 ? 'left' : 'right',
                                  `link${index}url`,
                                ),
                              );
                          }}
                          onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.stopPropagation();
                                searchStatisticsEventEmitter(
                                  widgetStatistics.sendUserRedirectionStatisticsEvent(
                                    `link${index}`,
                                    data.widgetType,
                                    data.widgetName,
                                    tabName,
                                    e.key,
                                    `link${index}url`,
                                  ),
                                );
                              }
                          }}
                          href={data[`link${index}url`]}
                        >
                          {data[`link${index}value`]}
                        </qds-standalone-link>
                      </li>
                    );
                  }
              })}
            </ul>
            <qds-button
              importance="emphasized"
              onMouseDown={(e) => {
                  e.stopPropagation();
                  searchStatisticsEventEmitter(
                    widgetStatistics.sendUserRedirectionStatisticsEvent(
                      'cta-button',
                      data.widgetType,
                      data.widgetName,
                      tabName,
                      e.button == 0 ? 'left' : 'right',
                      data.titleUrl,
                    ),
                  );
              }}
              onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    searchStatisticsEventEmitter(
                      widgetStatistics.sendUserRedirectionStatisticsEvent(
                        'cta-button',
                        data.widgetType,
                        data.widgetName,
                        tabName,
                        e.key,
                        data.titleUrl,
                      ),
                    );
                  }
              }}
              class="offites-cta-button"
            >
              {data.ctaButtonText}
            </qds-button>
          </div>
        </div>
      </div>
    );
};

export const apcWidget = (
  data: WidgetComponent['data'],
  searchStatisticsEventEmitter: WidgetComponent['searchStatisticsEventEmitter'],
  tabName: WidgetComponent['tabName'],
): FunctionalComponent => {
    const backgroundImage = getAssetPath(data.backgroundUrl);
    const productImage = getAssetPath(data.productImageUrl);
    const shadowImage = getAssetPath(data.shadowImageUrl);

    return (
      <div>
        <div
          class={['uiaas-offsites-widget', `type-${data.widgetType}`].join(' ')}
          style={{'background-image': data.backgroundUrl ? `url(${backgroundImage})` : 'none'}}
        ></div>
        <div class="contents">
          <ul>
            <li>
              <span innerHTML={data.titleValue} />
            </li>
            {[...Array(numberOfLinks)].map((_e, i) => {
                const index = i + 1;
                if (data[`link${index}value`]) {
                  return (
                    <li>
                      <qds-standalone-link
                        target="_blank"
                        rel="external"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            searchStatisticsEventEmitter(
                              widgetStatistics.sendUserRedirectionStatisticsEvent(
                                `link${index}`,
                                data.widgetType,
                                data.widgetName,
                                tabName,
                                e.button == 0 ? 'left' : 'right',
                                `link${index}url`,
                              ),
                            );
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              searchStatisticsEventEmitter(
                                widgetStatistics.sendUserRedirectionStatisticsEvent(
                                  `link${index}`,
                                  data.widgetType,
                                  data.widgetName,
                                  tabName,
                                  e.key,
                                  `link${index}url`,
                                ),
                              );
                            }
                        }}
                        href={data[`link${index}url`]}
                      >
                        {data[`link${index}value`]}
                      </qds-standalone-link>
                    </li>
                  );
                }
            })}
          </ul>
        </div>
        <div class="product-image__container">
          {!productImage.includes('undefined') && (
            <div
              style={{ 'background-image': `url(${productImage})` }}
              class={'product-image__image'}
            >
              <div
                class="shadow"
                style={{ 'background-image': `url(${shadowImage})` }}
              ></div>
            </div>
          )}
        </div>
      </div>
    );
};

// Special case for Aveva brand promotion
// To be removed when Aveva documents will be indexed!
export const avevaWidget = (
  data: WidgetComponent['data'],
  searchStatisticsEventEmitter: WidgetComponent['searchStatisticsEventEmitter'],
  tabName: WidgetComponent['tabName'],
): FunctionalComponent => {
    const backgroundImage = getAssetPath(data.backgroundUrl);

    return (
      <div
        class={['uiaas-offsites-widget', `type-${data.widgetType}`].join(' ')}
        style={{ 'background-image': `url(${backgroundImage})` }}
      >
        <section>
          <h3 class="offsites-title">
            <span class={'offsites-title__value'}>{data.titleValue}</span>{' '}
            <span class={'offsites-title__value-colored'}>
              {data.titleValueColored}
            </span>
          </h3>
          <p class='offsites-subtitle'>
            {data.subtitleValue}
          </p>
        </section>
        <div>
          <button
            onMouseDown={(e) => {
                e.stopPropagation();
                searchStatisticsEventEmitter(
                  widgetStatistics.sendUserRedirectionStatisticsEvent(
                    'cta-button',
                    data.widgetType,
                    data.widgetName,
                    tabName,
                    e.button == 0 ? 'left' : 'right',
                    data.titleUrl,
                  ),
                );
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.stopPropagation();
                  searchStatisticsEventEmitter(
                    widgetStatistics.sendUserRedirectionStatisticsEvent(
                      'cta-button',
                      data.widgetType,
                      data.widgetName,
                      tabName,
                      e.key,
                      data.titleUrl,
                    ),
                  );
                }
            }}
          >
            {data.ctaButtonText}
          </button>
        </div>
      </div>
    );
};

export const defaultWidget = (
  data: WidgetComponent['data'],
  searchStatisticsEventEmitter: WidgetComponent['searchStatisticsEventEmitter'],
  tabName: WidgetComponent['tabName'],
  isDashboardView: boolean,
): FunctionalComponent => {
    const backgroundImage = getAssetPath(data.backgroundUrl);
    const withoutLinksOrWithSingle = !data?.link2value && !data?.link3value;

    return (
      <div
        class={{
          'offsites-widget-default': !isDashboardView,
          'offsites-widget-default-dashboard': isDashboardView,
          'offsites-widget-default-dashboard--without-or-single-link':
          isDashboardView && withoutLinksOrWithSingle,
          'offsites-widget-default--large': data?.isLargeBanner,
          'offsites-widget-default--alternative': data?.isAlternativeVariant,
          'offsites-widget-default--alternative-half-width-subtitle':
          data?.isHalfWidthSubtitle,
          'offsites-widget-default--only-image': data?.onlyImage,
          'offsites-widget': !backgroundImage.includes('undefined'),
        }}
        style={{'background-image': data.backgroundUrl ? `url(${backgroundImage})` : 'none'}}
      >
        <section>
          <h3 class="offsites-title">
            <span class={'offsites-title__value'}>{data.titleValue}</span>{' '}
            <span class={'offsites-title__value-colored'}>
              {data.titleValueColored}
            </span>
          </h3>
          <p class="offsites-subtitle">{data.subtitleValue}</p>
        </section>
        <div class="offsites-link-section">
          <ul class={'offites-list'}>
            {[...Array(numberOfLinks)].map((_e, i) => {
                const index = i + 1;
                if (data[`link${index}value`]) {
                  return (
                    <li class={'offistes-list-element'}>
                      <qds-standalone-link
                        target="_blank"
                        class="offsites-link"
                        rel="external"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            searchStatisticsEventEmitter(
                              widgetStatistics.sendUserRedirectionStatisticsEvent(
                                `link${index}`,
                                data.widgetType,
                                data.widgetName,
                                tabName,
                                e.button == 0 ? 'left' : 'right',
                                `link${index}url`,
                              ),
                            );
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.stopPropagation();
                              searchStatisticsEventEmitter(
                                widgetStatistics.sendUserRedirectionStatisticsEvent(
                                  `link${index}`,
                                  data.widgetType,
                                  data.widgetName,
                                  tabName,
                                  e.key,
                                  `link${index}url`,
                                ),
                              );
                            }
                        }}
                        href={data[`link${index}url`]}
                      >
                        {data[`link${index}value`]}
                      </qds-standalone-link>
                    </li>
                  );
                }
            })}
          </ul>
          {data.ctaButtonText && (
            <qds-button
              importance="emphasized"
              onMouseDown={(e) => {
                  e.stopPropagation();
                  searchStatisticsEventEmitter(
                    widgetStatistics.sendUserRedirectionStatisticsEvent(
                      'cta-button',
                      data.widgetType,
                      data.widgetName,
                      tabName,
                      e.button == 0 ? 'left' : 'right',
                      data.titleUrl,
                    ),
                  );
              }}
              onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    searchStatisticsEventEmitter(
                      widgetStatistics.sendUserRedirectionStatisticsEvent(
                        'cta-button',
                        data.widgetType,
                        data.widgetName,
                        tabName,
                        e.key,
                        data.titleUrl,
                      ),
                    );
                  }
              }}
              text={data.ctaButtonText}
            ></qds-button>
          )}
        </div>
      </div>
    );
};

export interface IFullWidthWidget {
  data: widgetObject['data'];
  tabName: string;
  isDashboard: boolean;
  isRtl: boolean;
  computedPlacement: boolean;
  backgroundImagePath: string;
}

export const FullWidthWidget: FunctionalComponent<IFullWidthWidget> = (
  props,
) => {
    const {
      data,
      tabName,
      isDashboard,
      isRtl,
      computedPlacement,
      backgroundImagePath
    } = props;
    const setTitlePartialColor = data?.titleValueColored
      ? setTitlePartColor(data?.titleColor?.toLowerCase())
      : '';

    return (
      <a
        href={data.titleUrl}
        dir={isRtl ? 'rtl' : void 0}
        target="_blank"
        data-widget-area="background"
        class={{
          'offsite-banner': true,
          'offsite-banner--dashboard-view': isDashboard,
          'offsite-banner--next-to-widget': computedPlacement,
        }}
      >
        <section
          class={{
            'offsite-banner-content': true,
            'offsite-banner-content--with-image': Boolean(backgroundImagePath),
          }}
        >
          <section>
            <p class="offsite-banner-content__title">
              {data?.titleValue ? (
                <span class="offsite-banner-content__main-title">
                  {data.titleValue}
                </span>
              ) : ''}
              {data?.titleValueColored ? (
                <span
                  class={`offsite-banner-content__title-colored ${setTitlePartialColor}`}
                >
                  {data.titleValueColored}
                </span>
              ) : ''}
            </p>
            <p class="offsite-banner-content__subtitle">{data.subtitleValue}</p>
            {data?.ctaButtonText ? <qds-button
              data-widget-area="cta-button"
              size="large"
              importance="emphasized"
              text={data?.ctaButtonText}
              class="offsite-banner-content__cta-button"
            ></qds-button> : ''}
          </section>

          {data?.links?.length ? (
            <ul
              class={{
                'offsite-banner-list': true,
                'offsite-banner-content-links': true,
                'offsite-banner-content-links--with-image': Boolean(backgroundImagePath),
              }}
            >
              {data?.links.map((item, index) => (
                <li class="offsite-banner-list-element">
                  <qds-standalone-link
                    href={item.url}
                    target="_blank"
                    rel="external"
                    data-widget-area={`link${index + 1}`}
                    referrer-policy="no-referrer"
                  >
                    {item.value}
                  </qds-standalone-link>
                </li>
              ))}
            </ul>
          ) : (
            ''
          )}
        </section>
        {backgroundImagePath && tabName?.includes('all') ? (
          <section
            class="offsite-banner__image"
            style={{'background-image': backgroundImagePath ? `url(${backgroundImagePath})` : 'none'}}
          />
        ) : ''}
      </a>
    );
};
